/**
 * BuyTokensPage — Stripe Checkout for PM token purchases.
 *
 * Flow:
 *   1. Player picks a preset or custom amount.
 *   2. Click "Buy" → POST /api/billing/checkout → redirect to Stripe.
 *   3. After payment: Stripe redirects to /buy-tokens?status=success&session_id=...
 *   4. Webhook credits the balance; this page polls getPmBalance to show
 *      the new balance, then nav back to /dashboard.
 *
 * Trust: balance ONLY credits via the server-side webhook. The success
 * redirect is cosmetic — never trust it to mutate state.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Coins, ArrowLeft, Loader2, MessageCircle, Users } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useAccountModalStore } from '@/lib/stores/accountModalStore';
import { auth } from '@/lib/firebase';
import { toast } from '@/lib/toast';
import { usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { useAuthStore } from '@/lib/stores/authStore';
import {
  DISCORD_JOIN_GRANT_PM,
  MIN_PM_PURCHASE,
  PM_PURCHASE_PRESETS,
  REFERRAL_GRANT_PM,
  USD_PER_PM,
} from '@danda/shared/constants/billing';
import { DISCORD_INVITE_URL } from '@/lib/constants';

type PresetAmount = (typeof PM_PURCHASE_PRESETS)[number];

type Selection = PresetAmount | 'custom';

// localStorage key for the pre-purchase balance baseline. Captured in
// `handleBuy` before redirecting to Stripe; read back on `?status=success`
// as the poll baseline. Without this, if the Stripe webhook fires before
// the user's return-to-merchant redirect lands, the initial balance fetch
// on the success page already includes the credit — `baseline = balance`
// would be post-credit, and the `pollBalance > baseline` check never trips.
//
// Namespaced by Firebase uid so user A's stale baseline can't leak into
// user B's session if both sign in from the same browser. Returns null
// when no user is signed in (defensive — the route is auth-gated).
const PRE_PURCHASE_BALANCE_KEY_PREFIX = 'pmPurchase.preBalance.';
function getBaselineKey(): string | null {
  const uid = auth.currentUser?.uid;
  return uid ? `${PRE_PURCHASE_BALANCE_KEY_PREFIX}${uid}` : null;
}

export default function BuyTokensPage() {
  const navigate = useNavigate();
  const openAccount = useAccountModalStore((s) => s.openModal);
  const [searchParams] = useSearchParams();

  // Guests can't buy tokens — redirect to the dashboard convert card. The
  // backend also 403s anonymous checkout/iap; this is the UX chokepoint that
  // covers every /buy-tokens entry point at once.
  const isAnonymous = useAuthStore((s) => s.user?.isAnonymous ?? false);
  useEffect(() => {
    if (isAnonymous) navigate('/dashboard', { replace: true });
  }, [isAnonymous, navigate]);

  const [balance, setBalance] = useState<number | null>(null);
  const [balanceError, setBalanceError] = useState(false);
  const [selected, setSelected] = useState<Selection>(PM_PURCHASE_PRESETS[0]);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  // Once the success poll starts, freeze the baseline so subsequent re-renders
  // (which carry the newly-credited balance) don't reset it mid-poll.
  //
  // Refs (not state) — the polling effect's dep array includes `balance`, and
  // if this were state, calling `setSuccessPollStarted(true)` would re-render
  // the component, re-run the effect, fire the cleanup, and `clearInterval`
  // the just-created polling interval before its first 1s tick. Refs don't
  // trigger re-renders, so the interval survives.
  const successPollStartedRef = useRef(false);
  // Pending navigate('/dashboard') timer; ref-scoped (not effect-local) so
  // an unmount during the 1500ms delay can cancel the redirect even after
  // the effect re-ran on the balance state change that triggered it.
  const navTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cancel toast must fire once. The effect re-runs whenever `balance`
  // resolves, and status stays `cancel` across re-renders, so without this
  // guard the toast would double-fire after the initial balance loads.
  const [cancelToastShown, setCancelToastShown] = useState(false);

  const status = searchParams.get('status');
  const sessionId = searchParams.get('session_id');

  const pmAmount = useMemo(() => {
    if (selected === 'custom') {
      const n = Number(customAmount);
      return Number.isFinite(n) ? n : 0;
    }
    return selected;
  }, [selected, customAmount]);

  const usdAmount = pmAmount * USD_PER_PM;
  // Gating on `balance !== null` is intentional: handleBuy can only capture
  // the pre-purchase baseline once the balance has loaded. If we let the user
  // click "Buy" before that, no baseline gets written, and on the success-page
  // return the polling fallback (`balance - 0.01`) would treat the very first
  // poll as a credit and fire a fake $0.01 purchase event before the webhook
  // actually credits anything (Codex review finding #2).
  const submitDisabled =
    submitting || balance === null || !Number.isFinite(pmAmount) || pmAmount < MIN_PM_PURCHASE;

  // Initial balance load. Extracted so the "Unable to load balance" UI can
  // re-trigger it as a retry; without that, a transient fetch failure
  // permanently locks the Buy CTA (which requires `balance !== null` to
  // guarantee the baseline gets captured).
  const loadBalance = () => {
    setBalanceError(false);
    apiClient
      .getPmBalance()
      .then((data) => setBalance(data.pmTokenBalance))
      .catch((err) => {
        console.error('[BuyTokensPage] balance fetch failed:', err);
        setBalanceError(true);
      });
  };
  useEffect(() => {
    loadBalance();
    // loadBalance is defined inline and stable in semantics (no closure deps);
    // intentionally not adding to the deps array to avoid a re-mount loop.
  }, []);

  // GA4 ecommerce: page view of the PM tokens product.
  useEffect(() => {
    track('view_item', {
      currency: 'USD',
      value: 0,
      items: [{ item_id: 'pm_tokens', item_name: 'PM Tokens', price: USD_PER_PM }],
    });
  }, []);

  // Dedicated unmount cleanup for `navTimeoutRef`. The polling effect below
  // re-runs whenever its deps change (notably `balance` after the credit
  // landing call setBalance), and the re-run's early-return branch doesn't
  // return a cleanup function — so React replaces the prior cleanup with
  // `undefined` and an unmount during the 1500 ms navTimeout window would
  // leave the timer alive, firing `navigate('/dashboard')` on a torn-down
  // component. This unmount-only effect's cleanup always runs on unmount
  // because its deps array is empty (no re-runs, no cleanup replacement).
  useEffect(() => {
    return () => {
      if (navTimeoutRef.current !== null) {
        clearTimeout(navTimeoutRef.current);
        navTimeoutRef.current = null;
      }
    };
  }, []);

  // Handle Stripe redirect status.
  // For status=success, we wait until the initial balance fetch resolves
  // before starting the poll — otherwise the baseline would be `0` (the
  // null-coalesce fallback) and the first non-null balance would trigger the
  // "credited!" path even though no tokens actually arrived yet.
  useEffect(() => {
    if (status === 'cancel') {
      if (!cancelToastShown) {
        setCancelToastShown(true);
        toast.info('Purchase canceled', {
          description: 'No charge was made.',
        });
        track('pm_purchase_canceled');
      }
      return;
    }
    if (status !== 'success' || successPollStartedRef.current) return;
    if (balance === null) return; // wait for initial balance load

    successPollStartedRef.current = true;
    toast.success('Payment received', {
      description: 'Your PMT is on the way — refreshing your balance.',
    });

    // Read the pre-purchase baseline captured in handleBuy. If absent (page
    // loaded with ?status=success outside the normal flow), fall back to
    // current balance minus 0.01 so any positive delta still trips — safer
    // than using current balance directly when the webhook may have already
    // landed before the redirect.
    const baselineKey = getBaselineKey();
    const storedBaseline = baselineKey !== null ? localStorage.getItem(baselineKey) : null;
    const baseline = storedBaseline !== null ? parseFloat(storedBaseline) : balance - 0.01;
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const data = await apiClient.getPmBalance({ skipCache: true });
        if (data.pmTokenBalance > baseline) {
          setBalance(data.pmTokenBalance);
          // Hydrate the in-chat gate's store immediately. Without this,
          // ChatPanel reads the stale pre-purchase balance from
          // `usePmBalanceStore.pmBalance` until either the lobby WS pushes
          // `lobby:balance-update` or DashboardPage's own getPmBalance()
          // fetch resolves — leaving a window where the player tops up but
          // the first chat send still trips the gate.
          usePmBalanceStore.getState().setPmBalance(data.pmTokenBalance);
          clearInterval(interval);
          // The GA4 `purchase` event is server-owned: emitted from the Stripe
          // webhook's Credited arm (billing_checkout.rs) so revenue matches the
          // ledger exactly and can't be lost or double-counted by this client.
          // Consumed the baseline successfully — clear it so a reload of the
          // success page (or a future purchase reusing this tab) doesn't see
          // a stale entry.
          if (baselineKey !== null) localStorage.removeItem(baselineKey);
          // Capture the timer id so a mid-delay unmount cancels it instead of
          // calling navigate() on a torn-down component (React 18 logs a warning
          // and the redirect may steal an intentional navigation).
          navTimeoutRef.current = setTimeout(() => navigate('/dashboard'), 1500);
          // Done — don't also fall through to the `attempts >= 8` timeout
          // branch below. Without this, a credit landing on the 8th tick
          // would fire both `purchase` and `pm_purchase_processing` (Codex
          // review finding #4).
          return;
        }
      } catch (e) {
        console.error('[BuyTokensPage] post-success balance poll failed:', e);
      }
      if (attempts >= 8) {
        clearInterval(interval);
        // Give up on the baseline — clear it so it doesn't bleed into a
        // future purchase from the same tab.
        if (baselineKey !== null) localStorage.removeItem(baselineKey);
        toast.info('Still processing', {
          description: 'Tokens should appear shortly. Check your balance in a moment.',
        });
        if (sessionId) {
          track('pm_purchase_processing', { transaction_id: sessionId });
        }
      }
    }, 1000);
    // Only clear the polling interval here. `navTimeoutRef` is intentionally
    // owned by the unmount-only effect above — clearing it here would kill the
    // 1500 ms auto-nav on the re-render that follows `setBalance(...)` in the
    // credit-landed branch (the same setBalance that triggers this re-run).
    return () => {
      clearInterval(interval);
    };
  }, [status, balance, sessionId, cancelToastShown, navigate]);

  const handleBuy = async () => {
    if (submitDisabled) return;
    setSubmitting(true);
    track('begin_checkout', {
      currency: 'USD',
      value: usdAmount,
      items: [
        {
          item_id: 'pm_tokens',
          item_name: 'PM Tokens',
          price: USD_PER_PM,
          quantity: pmAmount,
        },
      ],
    });
    try {
      // Capture pre-purchase balance BEFORE the redirect so the success poll
      // has a stable baseline even if the webhook fires before Stripe returns
      // the user to /buy-tokens?status=success. Without this, a fast webhook
      // would make `baseline = current balance` post-credit, and the poll
      // would never observe an increase.
      const baselineKey = getBaselineKey();
      if (balance !== null && baselineKey !== null) {
        localStorage.setItem(baselineKey, String(balance));
      }
      const { url } = await apiClient.createPmCheckoutSession(pmAmount);
      window.location.href = url;
    } catch (err) {
      console.error('[BuyTokensPage] checkout create failed:', err);
      toast.error('Unable to start checkout', {
        description: err instanceof Error ? err.message : 'Please try again in a moment.',
      });
      track('pm_purchase_error', {
        error_message: err instanceof Error ? err.message : 'unknown',
        pm_amount: pmAmount,
      });
      setSubmitting(false);
    }
  };

  // Redirecting guests (see effect above) — don't flash the buy UI.
  if (isAnonymous) return null;

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-start justify-center px-4 py-10 sm:py-16 overflow-hidden">
      {/* Soft gold radial wash sets the fantasy-coin atmosphere without
          turning the page into a parchment cliché. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -10%, hsl(var(--accent-gold) / 0.18), transparent 65%)',
        }}
      />

      <div className="relative w-full max-w-xl">
        {/* Header */}
        <header className="text-center mb-9">
          <div className="relative inline-flex items-center justify-center mb-5">
            <div
              aria-hidden
              className="absolute inset-0 rounded-full blur-2xl"
              style={{ background: 'hsl(var(--accent-gold) / 0.35)' }}
            />
            <Coins
              className="relative h-11 w-11"
              style={{ color: 'hsl(var(--accent-gold))' }}
              strokeWidth={1.5}
            />
          </div>
          <h1 className="font-character text-4xl sm:text-5xl leading-tight tracking-tight text-foreground">
            Buy PMT
          </h1>
          <p className="font-narrative text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md mx-auto mt-4">
            PMT fund the magic — every AI-narrated scene, every NPC's voice, every twist of the
            tale. They're spent as you play:{' '}
            <span className="text-foreground">a few per round of combat</span>, fewer for quiet
            roleplay. Tokens never expire.
          </p>
        </header>

        {/* Main card */}
        <Card className="border-border/60 shadow-xl backdrop-blur-sm bg-card/95">
          <CardContent className="p-5 sm:p-7 space-y-6">
            {/* Balance pill */}
            <div className="flex items-center justify-center">
              {balance !== null ? (
                <div className="inline-flex items-baseline gap-2 px-3.5 py-1.5 rounded-full border border-border/60 bg-muted/40">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
                    Balance
                  </span>
                  <span
                    className={`font-system text-sm font-semibold ${
                      balance < 1 ? 'text-destructive' : 'text-foreground'
                    }`}
                  >
                    {balance.toFixed(2)} PMT
                  </span>
                </div>
              ) : balanceError ? (
                <button
                  type="button"
                  data-testid="balance-retry"
                  onClick={loadBalance}
                  className="text-xs text-destructive underline-offset-2 hover:underline"
                >
                  Unable to load balance — retry
                </button>
              ) : (
                <p className="text-xs text-muted-foreground animate-pulse">Loading balance…</p>
              )}
            </div>

            {/* Choose amount section */}
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Choose amount
                </h2>
                <span className="text-[11px] text-muted-foreground">
                  1 PMT = ${USD_PER_PM.toFixed(2)}
                </span>
              </div>

              {/* Preset tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PM_PURCHASE_PRESETS.map((preset: PresetAmount) => {
                  const isSelected = selected === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      data-testid={`preset-${preset}`}
                      onClick={() => setSelected(preset)}
                      aria-pressed={isSelected}
                      className={`rounded-lg border px-3 py-3 text-left transition-all ${
                        isSelected
                          ? 'border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10 shadow-sm'
                          : 'border-border/60 hover:border-border bg-card hover:bg-muted/40'
                      }`}
                    >
                      <div
                        className={`text-lg font-semibold leading-none ${
                          isSelected ? 'text-[hsl(var(--accent-gold))]' : 'text-foreground'
                        }`}
                      >
                        {preset}
                        <span className="text-[10px] font-medium ml-1 opacity-70 uppercase tracking-wider">
                          PM
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1.5">${preset}.00</div>
                    </button>
                  );
                })}
              </div>

              {/* Custom amount */}
              <button
                type="button"
                data-testid="custom-toggle"
                onClick={() => setSelected('custom')}
                aria-pressed={selected === 'custom'}
                className={`w-full rounded-lg border px-3 py-2.5 text-left transition-all ${
                  selected === 'custom'
                    ? 'border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/10'
                    : 'border-border/60 hover:border-border bg-card hover:bg-muted/40'
                }`}
              >
                {selected === 'custom' ? (
                  <div className="flex items-center gap-2">
                    <span className="font-system text-xs text-muted-foreground uppercase tracking-wider">
                      PM
                    </span>
                    <Input
                      type="number"
                      min={MIN_PM_PURCHASE}
                      step={1}
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder={`${MIN_PM_PURCHASE} or more`}
                      autoFocus
                      className="flex-1 h-7 border-0 bg-transparent focus-visible:ring-0 px-0 font-system text-base"
                    />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      = ${usdAmount.toFixed(2)}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Custom amount…</span>
                )}
              </button>
            </div>

            {/* Primary CTA */}
            <Button
              type="button"
              data-testid="buy-cta"
              onClick={handleBuy}
              disabled={submitDisabled}
              className="w-full h-11 text-sm font-semibold tracking-tight"
              style={
                !submitDisabled
                  ? {
                      background: 'hsl(var(--accent-gold))',
                      color: 'hsl(33 40% 12%)',
                    }
                  : undefined
              }
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : pmAmount >= MIN_PM_PURCHASE ? (
                <>Continue to checkout · ${usdAmount.toFixed(2)}</>
              ) : (
                <>Choose at least {MIN_PM_PURCHASE} PM</>
              )}
            </Button>

            <p className="text-[11px] text-center text-muted-foreground leading-relaxed">
              Secure checkout via Stripe · Cards accepted · Tokens credit instantly
            </p>
          </CardContent>
        </Card>

        {/* Earn-tokens-via-feedback CTA. Especially the out-of-tokens path: a
            player low on / out of PM can join Discord, share feedback, and we
            gift bonus tokens (manual grant, once per account — claimed by
            posting feedback). Headline sharpens when balance < 1 PM. */}
        <Card className="mt-4 border-border/60 bg-card/80 backdrop-blur-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div
                aria-hidden
                className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                style={{ background: 'hsl(235 86% 65% / 0.14)' }}
              >
                <MessageCircle
                  className="h-5 w-5"
                  style={{ color: 'hsl(235 86% 68%)' }}
                  strokeWidth={1.75}
                />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-foreground">
                  {balance !== null && balance < 1
                    ? 'Out of tokens? Earn more, free.'
                    : 'Prefer to earn tokens?'}
                </h3>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                  Bring a friend and you each get {REFERRAL_GRANT_PM} PMT, enough to play Goldvale
                  through. Party costs split between you, so it stretches further too. Joining our
                  Discord earns {DISCORD_JOIN_GRANT_PM} PMT, and sharing feedback there earns more.
                </p>
                <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      track('invite_cta_click', {
                        surface: 'buy_tokens',
                        low_balance: balance !== null && balance < 1,
                      });
                      openAccount('invite');
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(235_86%_68%)] underline-offset-2 hover:underline"
                  >
                    <Users className="h-3.5 w-3.5" />
                    Invite friends
                  </button>
                  <a
                    href={DISCORD_INVITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="discord-feedback-cta"
                    onClick={() =>
                      track('discord_feedback_cta_click', {
                        surface: 'buy_tokens',
                        low_balance: balance !== null && balance < 1,
                      })
                    }
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    Join Discord &amp; give feedback
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back link */}
        <div className="text-center mt-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
